import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from "@/hooks/useI18n";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useAgencyAdvisorMembers } from "@/hooks/useAgencyAdvisorMembers";
import { useClientsListExport } from "@/hooks/useClientsListExport";
import { ClientsFilters } from "@/lib/dashboard/clients/ClientsFilters";
import { ClientsEmpty, ClientsTable } from "@/lib/dashboard/clients/ClientsTable";
import { clientDisplayName } from "@/lib/dashboard/clients/client-display";
import { ClientDetailSheet } from "@/lib/dashboard/clients/ClientDetailSheet";
import {
  AgencyClientRow,
  AgencyDashboardPeriod,
} from "@/integrations/supabase/rpc-types";

interface ClientsTabProps {
  period: AgencyDashboardPeriod;
  /**
   * Cliente da aprire all'ingresso nella tab, usato dal salto dalla scheda di un
   * consulente. La riga corrispondente puo' non essere nella lista corrente (filtri
   * diversi): il pannello carica comunque dalla RPC, che non dipende dalla lista.
   */
  openClientKey?: string | null;
  /** Segnala che la richiesta di apertura e' stata consumata. */
  onOpenedClient?: () => void;
  /**
   * Vero per admin e agency: sono gli unici che vedono i clienti di piu' consulenti, e
   * quindi gli unici per cui "di chi e' questo cliente" e' una domanda sensata.
   */
  canFilterByAdvisor: boolean;
}

export function ClientsTab({
  period,
  canFilterByAdvisor,
  openClientKey,
  onOpenedClient,
}: ClientsTabProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  // La selezione e' la chiave, non la riga: cosi' il pannello puo' essere aperto anche
  // per un cliente che nella lista filtrata non c'e'.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Vive qui e non nello sheet, ma torna sempre a false quando il pannello si chiude:
  // la scheda deve aprirsi sullo storico completo, non ereditare il filtro dal cliente
  // guardato prima.
  const [periodOnly, setPeriodOnly] = useState(false);

  useEffect(() => {
    if (openClientKey) {
      setSelectedKey(openClientKey);
      onOpenedClient?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openClientKey]);

  const { data, loading, refreshing, error, refresh } = useAgencyClients({
    period,
    designerId: advisorId,
    search,
  });

  // Le opzioni del filtro escono dalla stessa RPC della tab Advisor Members: sono per
  // definizione i consulenti del perimetro di chi guarda, gia' filtrati dal database.
  const { data: advisors } = useAgencyAdvisorMembers(period, canFilterByAdvisor);

  const hasFilters = Boolean(search.trim()) || advisorId !== null;

  const { exporting, exportClients } = useClientsListExport();

  // Il nome del consulente filtrato finisce nel foglio "Contesto" del file: senza, un
  // export ristretto a una persona sarebbe indistinguibile da uno completo.
  const selectedAdvisor = advisors.find((advisor) => advisor.advisor_id === advisorId);
  const selectedAdvisorName = selectedAdvisor
    ? [selectedAdvisor.first_name, selectedAdvisor.last_name].filter(Boolean).join(" ") ||
      selectedAdvisor.email
    : null;

  // Anagrafica per l'intestazione mentre la scheda carica. Assente quando si arriva da un
  // salto esterno: il pannello riempie il vuoto appena risponde la RPC.
  const selectedRow = useMemo(
    () => data.find((row) => row.client_key === selectedKey),
    [data, selectedKey]
  );
  const selectedName = selectedRow ? clientDisplayName(selectedRow) : undefined;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center gap-2 py-10 text-gray-500 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">{t("dashboard.overview.clients.loading")}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {t("dashboard.overview.errorTitle")}
              </p>
              <p className="text-sm text-gray-500">
                {t("dashboard.overview.errorDescription")}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refresh()} className="mt-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("dashboard.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <ClientsFilters
        search={search}
        onSearchChange={setSearch}
        advisorId={advisorId}
        onAdvisorChange={setAdvisorId}
        advisors={canFilterByAdvisor ? advisors : undefined}
        isRefreshing={refreshing}
        onRefresh={refresh}
        isExporting={exporting}
        canExport={data.length > 0}
        onExport={() =>
          exportClients({
            rows: data,
            period,
            search,
            advisorName: selectedAdvisorName,
          })
        }
      />

      <p className="text-sm text-gray-500">
        {t("dashboard.overview.clients.count", { count: data.length })}
      </p>

      {data.length === 0 ? (
        <ClientsEmpty filtered={hasFilters} />
      ) : (
        // Il velo durante il refresh tiene la tabella a schermo mentre la ricerca gira:
        // sostituirla con uno spinner farebbe lampeggiare la pagina a ogni lettera.
        <div className={refreshing ? "opacity-60 transition-opacity" : undefined}>
          <ClientsTable
            rows={data}
            selectedKey={selectedKey}
            onSelect={(row) => setSelectedKey(row.client_key)}
            showAdvisorColumn={canFilterByAdvisor}
          />
        </div>
      )}

      <ClientDetailSheet
        clientKey={selectedKey}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedKey(null);
            setPeriodOnly(false);
          }
        }}
        period={period}
        periodOnly={periodOnly}
        onPeriodOnlyChange={setPeriodOnly}
        fallbackName={selectedName}
        fallbackEmail={selectedRow?.email}
        advisorFilterActive={advisorId !== null}
      />
    </div>
  );
}

export default ClientsTab;
